import React, {
  lazy, Suspense, useContext, useLayoutEffect, useState, useSyncExternalStore,
} from 'react';
import usePreviewStore from '$/store/usePreviewStore';
import { MyIpMediaPreviewHostContext } from '$/components/MyIpMediaPreview/hostContext';

const MediaPreview = lazy(() => import('$/components/MediaPreview'));
const fallbackHosts = new Set<symbol>();
const hostSubscribers = new Set<() => void>();

/** 并列挂载的独立 SDK 视图共用首个有效宿主，避免全局会话打开多份播放器。 */
const getFallbackHost = () => fallbackHosts.values().next().value as symbol | undefined;
/** 服务端不挂载播放器；客户端在布局阶段注册实际宿主。 */
const getServerHost = () => undefined;
/** 注册对宿主交接的订阅，卸载时释放 React 更新回调。 */
const subscribeHost = (listener: () => void) => {
  hostSubscribers.add(listener);
  return () => { hostSubscribers.delete(listener); };
};
/** 同步通知宿主选择变化，正在打开的会话只交由一份 fallback 呈现。 */
const notifyHosts = () => hostSubscribers.forEach((listener) => listener());

/** 独立 SDK 仅按需加载公共播放器；嵌套及并列的 IP 视图均共用一份宿主。 */
export function MyIpMediaPreviewBoundary({ children }: { children: React.ReactNode; }) {
  const hosted = useContext(MyIpMediaPreviewHostContext);
  const open = usePreviewStore((state) => state.openPreview || state.openMobilePreview);
  const [hostId] = useState(() => Symbol('my-ip-media-preview-host'));
  const selectedHost = useSyncExternalStore(subscribeHost, getFallbackHost, getServerHost);
  useLayoutEffect(() => {
    if (hosted) return undefined;
    fallbackHosts.add(hostId);
    notifyHosts();
    return () => {
      fallbackHosts.delete(hostId);
      notifyHosts();
    };
  }, [hosted, hostId]);
  return (
    <MyIpMediaPreviewHostContext.Provider value>
      {children}
      {!hosted && selectedHost === hostId && open && <Suspense fallback={null}><MediaPreview /></Suspense>}
    </MyIpMediaPreviewHostContext.Provider>
  );
}
